/**
 * useSupplierDetailHandlers
 * 협력사 상세 정보 핸들러 훅
 */
import {useCallback, useState} from 'react';
import {useToast} from '@/shared/components/feedback/Toast/ToastContext';
import {ToastType} from '@/shared/components/feedback/Toast/Toast.types';
import { supplierApi } from '../api/supplierApi';
import { SupplierInfo } from '../types/supplierInfo.types';
import { supplierInfoFromDto } from '../utils/supplierHelpers';
import {
    cleanBusinessNumber,
    cleanPhoneNumber,
    validateBusinessInfo,
} from '../utils/supplierDetailHelpers';

interface UseSupplierDetailHandlersProps {
    supplierInfo: SupplierInfo | null;
    setSupplierInfo: (info: SupplierInfo | null) => void;
}

export const useSupplierDetailHandlers = ({
                                              supplierInfo,
                                              setSupplierInfo
                                          }: UseSupplierDetailHandlersProps) => {
    const { showToast } = useToast();
    const [saving, setSaving] = useState(false);

    /**
     * 정산 정보 저장
     * 은행코드, 계좌번호, 예금주 저장
     */
    const handleSaveSettlementInfo = useCallback(async () => {
        if (!supplierInfo) return;

        // 유효성 검사
        if (!supplierInfo.bankCode || !supplierInfo.accountNumber || !supplierInfo.accountHolder) {
            showToast('모든 정산 정보를 입력해주세요.', { type: ToastType.ERROR });
            return;
        }

        if (!window.confirm('정산 정보를 저장하시겠습니까?')) {
            return;
        }

        try {
            setSaving(true);

            await supplierApi.updateSupplierSettlement(
                supplierInfo.supplierId,
                {
                    bankCode: supplierInfo.bankCode,
                    accountNum: supplierInfo.accountNumber,
                    acctName: supplierInfo.accountHolder
                }
            );

            // 저장 후 최신 데이터 다시 조회
            const response = await supplierApi.getSupplierInfo(supplierInfo.supplierId);
            setSupplierInfo(supplierInfoFromDto(response.supplier));

            showToast('정산 정보가 저장되었습니다.', { type: ToastType.SUCCESS });
        } catch (error) {
            showToast('정산 정보 저장에 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setSaving(false);
        }
    }, [supplierInfo, showToast, setSupplierInfo]);

    /**
     * 사업자 정보 저장
     * 법인명, 사업자등록번호, 대표자명 저장
     */
    const handleSaveBusinessInfo = useCallback(async () => {
        if (!supplierInfo) return;

        if (!window.confirm('사업자 정보를 저장하시겠습니까?')) {
            return;
        }

        // 유효성 검사
        if (!validateBusinessInfo(
            supplierInfo.companyName,
            supplierInfo.businessNumber,
            supplierInfo.representative
        )) {
            showToast('모든 사업자 정보를 입력해주세요.', { type: ToastType.ERROR });
            return;
        }

        try {
            setSaving(true);

            await supplierApi.updateSupplierBusinessInfo(
                supplierInfo.supplierId,
                {
                    suppCompName: supplierInfo.companyName,
                    suppNum: cleanBusinessNumber(supplierInfo.businessNumber),
                    compCeoName: supplierInfo.representative,
                    bizType: supplierInfo.bizType,
                    bizItem: supplierInfo.bizItem,
                    bizClass: supplierInfo.bizClass,
                    taxType: supplierInfo.taxType
                }
            );

            // 저장 후 최신 데이터 다시 조회
            const response = await supplierApi.getSupplierInfo(supplierInfo.supplierId);
            setSupplierInfo(supplierInfoFromDto(response.supplier));

            showToast('사업자 정보가 저장되었습니다.', { type: ToastType.SUCCESS });
        } catch (error) {
            showToast('사업자 정보 저장에 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setSaving(false);
        }
    }, [supplierInfo, showToast, setSupplierInfo]);

    /**
     * 협력사 기본 정보 저장 헨들러
     * 기본 정보 + 등급/세트수 + 정산 정보를 함께 저장
     */
    const handleSaveBasicInfo = useCallback(async () => {
        if (!supplierInfo) return;

        if (!window.confirm('기본 정보를 저장하시겠습니까?')) return;

        setSaving(true);
        try {
            await supplierApi.updateSupplierBasicInfo(supplierInfo.supplierId, {
                mngPhoneNum: cleanPhoneNumber(supplierInfo.mobilePhone),
                addrInfo: supplierInfo.address,
                suppType: supplierInfo.suppType,
                suppLvl: supplierInfo.suppLvl || undefined,
                slotSetCnt: supplierInfo.slotSetCnt ? Number(supplierInfo.slotSetCnt) : undefined
            });

            const response = await supplierApi.getSupplierInfo(supplierInfo.supplierId);
            setSupplierInfo(supplierInfoFromDto(response.supplier));

            showToast('기본 정보가 저장되었습니다.', { type: ToastType.SUCCESS });
        } catch {
            showToast('기본 정보 저장에 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setSaving(false);
        }
    }, [supplierInfo, showToast, setSupplierInfo]);

    /**
     * 필드 변경 핸들러
     * @param field 변경할 필드명 또는 여러 필드를 포함한 객체
     * @param value 새로운 값 (field가 객체인 경우 무시됨)
     */
    const handleFieldChange = useCallback((field: keyof SupplierInfo | Partial<SupplierInfo>, value?: string) => {
        if (!supplierInfo) return;

        // 여러 필드를 한 번에 업데이트하는 경우
        if (typeof field === 'object') {
            setSupplierInfo({ ...supplierInfo, ...field });
        } else {
            // 단일 필드 업데이트
            setSupplierInfo({ ...supplierInfo, [field]: value });
        }
    }, [supplierInfo, setSupplierInfo]);

    return {
        saving,
        handleSaveSettlementInfo,
        handleSaveBusinessInfo,
        handleSaveBasicInfo,
        handleFieldChange
    };
};
